import React, { useState } from "react";
import { motion } from "framer-motion";
import { 
  Mail, 
  Home, 
  Phone, 
  GraduationCap, 
  Calendar, 
  Building, 
  BookOpen,
  User 
} from "lucide-react";
import { Snackbar, Alert } from "@mui/material"; // ✅ Import Snackbar & Alert

import httpClient from  "../Api/axios";

const Form = () => {
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    email: "",
    dob: "", 
    mobileNo: "", 
    collegeName: "", 
    degree: "",
    academicYear: "",
    selectedCourse: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateForm = () => {
    let tempErrors = {};
    if (!formData.name.trim()) tempErrors.name = "Name is required";
    if (!formData.address.trim()) tempErrors.address = "Address is required";
    if (!formData.email.match(/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/)) tempErrors.email = "Valid email required";
    if (!formData.dob) tempErrors.dob = "Date of Birth is required";
    if (!formData.mobileNo.match(/^\d{10}$/)) tempErrors.mobileNo = "Valid 10-digit mobile required";  // ✅ Fixed field name
    if (!formData.collegeName.trim()) tempErrors.collegeName = "College name required"; // ✅ Fixed field name
    if (!formData.degree.trim()) tempErrors.degree = "Degree is required";
    if (!formData.academicYear.trim()) tempErrors.academicYear = "Academic year required";
    if (!formData.selectedCourse) tempErrors.selectedCourse = "Select a course";
  
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (validateForm()) {
      try {
        const formattedData = {
          ...formData,
          dob: new Date(formData.dob).toLocaleDateString("en-GB"), // Convert YYYY-MM-DD to DD/MM/YYYY
        };
  
        const response = await httpClient.post(
          "/api/ApplicationFormMaster/create", 
          formattedData 
        );
  
        console.log("Form submitted successfully:", response.data);
  
        setIsSubmitted(true);
        setFormData({
          name: "",
          address: "",
          email: "",
          dob: "",
          mobileNo: "",
          collegeName: "",
          degree: "",
          academicYear: "",
          selectedCourse: "",
        });
  
        setTimeout(() => setIsSubmitted(false), 3000);
      } catch (error) {
        console.error("Error submitting form:", error.response ? error.response.data : error.message);
      }
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-cyan-50 to-blue-100 p-4 md:p-6">
      <motion.div 
        initial={{ opacity: 0, y: 50 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ duration: 0.6, type: "spring", stiffness: 50 }}
        className="w-full max-w-lg md:max-w-3xl bg-white shadow-2xl rounded-3xl overflow-hidden transform transition-all duration-300 hover:scale-[1.02]"
      >
        <div className="bg-[#ff9000] p-4 md:p-6 text-white flex flex-col md:flex-row items-center justify-center md:justify-start space-y-4 md:space-y-0 md:space-x-4">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
            <img src="./img/Logo1.png" alt="Logo" className="w-10 h-10" />
          </div>
          <div className="text-center md:text-left">
            <h1 className="text-xl md:text-3xl font-bold tracking-wide text-black">e-DEESHA Technologies</h1>
            <p className="text-sm md:text-xl opacity-80 text-black">Student Registration Portal</p>
          </div>
        </div>
        <div className="p-8 space-y-6">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">Student Registration Form</h2>

          <form   onSubmit={handleSubmit}  className="space-y-6">
            {/* Name and Address Row */}
            <div className="grid md:grid-cols-2 gap-6">
              <InputField icon={User} name="name" label=" Full Name" value={formData.name} setFormData={setFormData} error={errors.name} />
              <InputField icon={Home} name="address" label="Address" value={formData.address} setFormData={setFormData} error={errors.address} />
            </div>

            {/* Email and DOB Row */}
            <div className="grid md:grid-cols-2 gap-6">
              <InputField icon={Mail} name="email" label="Email" value={formData.email} setFormData={setFormData} error={errors.email} />
              <InputField icon={Calendar} name="dob" label="Date of Birth" type="date" value={formData.dob} setFormData={setFormData} error={errors.dob} />
            </div>

            {/* Mobile and College Row */}
            <div className="grid md:grid-cols-2 gap-6">
            <InputField icon={Phone} name="mobileNo" label="Mobile No" type="tel" value={formData.mobileNo} setFormData={setFormData} error={errors.mobileNo} />
            <InputField icon={GraduationCap} name="collegeName" label="College Name" value={formData.collegeName} setFormData={setFormData} error={errors.collegeName} />

            </div>

            {/* Degree and Academic Year Row */}
            <div className="grid md:grid-cols-2 gap-6">
              <InputField icon={Building} name="degree" label="Qualification" value={formData.degree} setFormData={setFormData} error={errors.degree} />
              <InputField icon={Calendar} name="academicYear" label="Academic Year" value={formData.academicYear} setFormData={setFormData} error={errors.academicYear} />
            </div>

            {/* Selected Course */}
            <InputField icon={BookOpen} name="selectedCourse" label="Selected Course" type="select" value={formData.selectedCourse} setFormData={setFormData} error={errors.selectedCourse} />
            <div className="flex justify-center mt-4">
  <button 
    type="submit"
  
    className="px-6 py-4 text-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-black font-bold rounded-xl 
               hover:from-cyan-600 hover:to-blue-700 
               transition duration-300 transform hover:scale-[1.05] 
               shadow-lg hover:shadow-xl"
  >
    Submit
  </button>
</div>


          </form>
        </div>
      </motion.div>
      <Snackbar 
  open={isSubmitted} 
  autoHideDuration={3000} 
  onClose={() => setIsSubmitted(false)}
  anchorOrigin={{ vertical: "top", horizontal: "center" }} // ✅ Centered Snackbar
>
  <Alert 
    onClose={() => setIsSubmitted(false)} 
    severity="success" 
    variant="filled"
    sx={{
      fontSize: { xs: "1rem", md: "1.2rem" },  // ✅ Adjust font size for mobile
      padding: { xs: "15px 20px", md: "20px 30px" }, // ✅ Smaller padding on mobile
      borderRadius: "10px",
      minWidth: { xs: "250px", md: "300px" }, // ✅ Responsive width
      display: "flex",
      justifyContent: "center",
      textAlign: "center",
      marginTop: { xs: "200px", md: "400px" }, // ✅ Adjust margin for mobile
    }}
  >
    🎉 Form submitted successfully!
  </Alert>
</Snackbar>


    </div>
  );
};

const InputField = ({ icon: Icon, name, label, type = "text", value, setFormData, error }) => (
  <div>
    <label className="block mb-2 text-sm font-medium text-gray-700 tracking-wider">{label}</label>
    <div className={`flex items-center border-2 rounded-lg p-2.5 transition duration-300 
      ${error 
        ? 'border-red-400 focus-within:ring-2 focus-within:ring-red-300' 
        : 'border-gray-300 focus-within:border-cyan-500 focus-within:ring-2 focus-within:ring-cyan-200'
      }`}>
      <Icon className="text-gray-500 mr-3" size={20} />
      {type === "select" ? (
        <select 
          name={name} 
          value={value} 
          onChange={(e) => setFormData((prev) => ({ ...prev, [name]: e.target.value }))} 
          className="w-full outline-none text-gray-700 bg-transparent"
        >
          <option value="">Select a Course</option>
          <option value="web-development">C Programmming</option>
          <option value="C++">C++</option>
          <option value="core java">Core java</option>
          <option value="Advance java">Advance java</option>
          <option value="Full Stack Development">Full Stack Development</option>
          <option value="python">python</option>
          <option value="DBMS">DBMS</option>
          <option value="Data structure">Data structure</option>
          <option value="Software Testing">Software Testing</option>

        </select>
      ) : (
        <input 
          type={type} 
          name={name} 
          value={value} 
          onChange={(e) => setFormData((prev) => ({ ...prev, [name]: e.target.value }))} 
          className="w-full outline-none text-gray-700 bg-transparent" 
        />
      )}
    </div>
    {error && <p className="text-red-500 text-xs mt-1.5 pl-1 tracking-wide">{error}</p>}
  </div>
);

export default Form;